import { ref, onUnmounted } from 'vue'

export type DroppedProjectFile = {
	Id: number
	Name: string
	Signed: boolean
}

export type FileValidationResult = {
	isValid: boolean
	error?: string
}

type UseFileDropOptions = {
	maxSizeMb?: number
	multiple?: boolean
	onFiles?: (files: File[]) => void | Promise<void>
	onProjectFile?: (parsed: DroppedProjectFile) => void | Promise<void>
	onError?: (message: string) => void
}

export function useFileDrop(options: UseFileDropOptions) {
	const {
		maxSizeMb = 100,
		multiple = false,
		onFiles,
		onProjectFile,
		onError,
	} = options

	const isDraggingOver = ref(false)
	const canDrop = ref(false)
	const dragEnterCounter = ref(0)
	let dragLeaveTimer: ReturnType<typeof setTimeout> | null = null

	const reportError = (message: string) => {
		if (onError) onError(message)
		else console.error(message)
	}

	const validateFile = (file: File): FileValidationResult => {
		if (file.size / (1024 * 1024) > maxSizeMb) {
			return {
				isValid: false,
				error: `Файл слишком большой. Максимальный размер: ${maxSizeMb}MB`,
			}
		}

		if (file.name === '' || file.size === 0) {
			return {
				isValid: false,
				error: 'Папки не поддерживаются. Пожалуйста, выберите файл',
			}
		}

		return { isValid: true }
	}

	const resetDragState = () => {
		isDraggingOver.value = false
		canDrop.value = false
		dragEnterCounter.value = 0
	}

	const handleDragOver = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (!event.dataTransfer) return
		event.dataTransfer.dropEffect = 'copy'
	}

	const handleDragEnter = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (!event.dataTransfer) return

		if (dragEnterCounter.value === 0) {
			isDraggingOver.value = true
		}

		dragEnterCounter.value++
		canDrop.value = true

		if (dragLeaveTimer) {
			clearTimeout(dragLeaveTimer)
			dragLeaveTimer = null
		}
	}

	const handleDragLeave = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		dragEnterCounter.value--

		if (dragEnterCounter.value <= 0) {
			dragLeaveTimer = setTimeout(() => {
				resetDragState()
			}, 10)
		}
	}

	const handleDrop = async (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (dragLeaveTimer) {
			clearTimeout(dragLeaveTimer)
			dragLeaveTimer = null
		}

		resetDragState()

		if (!event.dataTransfer) {
			reportError('Ошибка при перетаскивании файла')
			return
		}

		const jsonData = event.dataTransfer.getData('application/json')
		if (jsonData) {
			if (!onProjectFile) return
			try {
				const parsed = JSON.parse(jsonData) as DroppedProjectFile
				await onProjectFile(parsed)
			} catch (e) {
				console.error(e)
			}
			return
		}

		if (!event.dataTransfer.types.includes('Files')) {
			reportError('Перетаскивание папок не поддерживается')
			return
		}

		const incomingFiles = Array.from(event.dataTransfer.files)
		const items = Array.from(event.dataTransfer.items)
		const hasFolders = items.some(item => {
			const getAsEntry = (item as any).webkitGetAsEntry as
				| (() => { isDirectory?: boolean } | null)
				| undefined
			if (typeof getAsEntry === 'function') {
				const entry = getAsEntry.call(item)
				return !!entry && !!entry.isDirectory
			}
			const file = item.getAsFile()
			return !file || file.name === '' || file.size === 0
		})

		if (hasFolders) {
			reportError('Перетаскивание папок не поддерживается. Пожалуйста, выберите файлы')
			return
		}

		if (incomingFiles.length === 0) {
			reportError('Не удалось получить файлы')
			return
		}

		if (!multiple && incomingFiles.length > 1) {
			reportError('Пожалуйста, перетащите только один файл')
			return
		}

		const accepted: File[] = []
		for (const file of incomingFiles) {
			const validation = validateFile(file)
			if (!validation.isValid) {
				reportError(validation.error!)
				if (!multiple) return
				continue
			}
			accepted.push(file)
		}

		if (accepted.length === 0) return
		if (onFiles) await onFiles(accepted)
	}

	onUnmounted(() => {
		if (dragLeaveTimer) {
			clearTimeout(dragLeaveTimer)
			dragLeaveTimer = null
		}
	})

	return {
		isDraggingOver,
		canDrop,
		validateFile,
		resetDragState,
		handleDragOver,
		handleDragEnter,
		handleDragLeave,
		handleDrop,
	}
}
